/**
 * adminApi — typed fetch wrappers for all /api/admin/* endpoints.
 *
 * Isolation contract:
 *  - This is the ONLY file in the frontend that calls /api/admin/* routes.
 *  - It shares the same base URL and auth token approach as the main api client
 *    but is a completely separate module so the admin surface can be audited here.
 *  - No business logic from the main app should import from this file.
 */
import axios, { AxiosInstance } from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8003/api";

function getToken(): string | null {
  return (
    localStorage.getItem("avahana-token") || localStorage.getItem("token")
  );
}

class AdminApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/admin`,
      timeout: 30000,
      headers: { "Content-Type": "application/json" },
    });

    this.client.interceptors.request.use((config) => {
      const token = getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      // Never cache admin responses
      config.headers["Cache-Control"] = "no-store";
      return config;
    });

    this.client.interceptors.response.use(
      (r) => r,
      (error) => {
        // 401: session expired
        if (error.response?.status === 401) {
          localStorage.removeItem("avahana-token");
          localStorage.removeItem("token");
          window.location.href = "/auth/login";
        }
        // 403 on admin routes: role was revoked — send to home
        if (error.response?.status === 403) {
          window.location.href = "/";
        }
        return Promise.reject(error);
      }
    );
  }

  private async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const response = await this.client.get<T>(path, { params });
    return response.data;
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    const response = await this.client.patch<T>(path, body);
    return response.data;
  }

  private async del<T>(path: string): Promise<T> {
    const response = await this.client.delete<T>(path);
    return response.data;
  }

  private async post<T>(path: string, body?: unknown): Promise<T> {
    const response = await this.client.post<T>(path, body ?? {});
    return response.data;
  }

  private async put<T>(path: string, body: unknown): Promise<T> {
    const response = await this.client.put<T>(path, body);
    return response.data;
  }

  // ── Platform settings ───────────────────────────────────────────────────

  // Toggle whether the "connect with full access (OAuth)" git option is
  // offered platform-wide. Admin only (403 for regular users).
  updateGitSettings(body: { github_oauth_connect_enabled: boolean }) {
    return this.put<{ success: boolean }>("/settings/git", body);
  }

  // ── Stats ────────────────────────────────────────────────────────────────

  stats() {
    return this.get<{
      users: { total: number; active: number; thisMonth: number };
      projects: { total: number };
      services: { total: number; running: number; failed: number; building: number };
      deployments: { total: number; successRate: number; avgBuildSeconds: number };
    }>("/stats");
  }

  // ── Users ────────────────────────────────────────────────────────────────

  createUser(body: { email: string; name?: string; password: string; role?: "user" | "admin" }) {
    return this.post<{ id: string; email: string; name?: string; role: string }>("/users", body);
  }

  listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    role?: string;
    sort?: string;
    order?: "asc" | "desc";
  }) {
    return this.get<{ users: AdminUser[]; total: number; page: number; limit: number }>(
      "/users",
      params
    );
  }

  getUser(id: string) {
    return this.get<{
      user: AdminUser;
      projects: AdminProject[];
      recentDeployments: AdminDeployment[];
    }>(`/users/${id}`);
  }

  updateUser(id: string, body: {
    role?: "user" | "admin";
    accountStatus?: "active" | "suspended";
    name?: string;
    email?: string;
  }) {
    return this.patch<{ message: string }>(`/users/${id}`, body);
  }

  deleteUser(id: string) {
    return this.del<{ message: string }>(`/users/${id}`);
  }

  // ── Projects ─────────────────────────────────────────────────────────────

  listProjects(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    search?: string;
    ownerEmail?: string;
    sort?: string;
    order?: "asc" | "desc";
  }) {
    return this.get<{ projects: { project: AdminProject; ownerEmail: string; serviceCount: number }[]; total: number; page: number; limit: number }>(
      "/projects",
      params
    );
  }

  deleteProject(id: string) {
    return this.del<{ message: string }>(`/projects/${id}`);
  }

  // ── Services ─────────────────────────────────────────────────────────────

  listServices(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    status?: string;
    type?: string;
    search?: string;
    ownerEmail?: string;
    projectName?: string;
    projectId?: string;
    sort?: string;
    order?: "asc" | "desc";
  }) {
    return this.get<{ services: AdminService[]; total: number; page: number; limit: number }>(
      "/services",
      params
    );
  }

  stopService(id: string) {
    return this.post<{ message: string }>(`/services/${id}/stop`);
  }

  deleteService(id: string) {
    return this.del<{ message: string }>(`/services/${id}`);
  }

  // ── Deployments ──────────────────────────────────────────────────────────

  listDeployments(params?: {
    page?: number;
    limit?: number;
    status?: string;
    userId?: string;
    serviceId?: string;
    sort?: string;
    order?: "asc" | "desc";
    createdAfter?: string;
    createdBefore?: string;
    enrich?: boolean;
  }) {
    return this.get<{ deployments: AdminDeployment[]; total: number; page: number; limit: number }>(
      "/deployments",
      params
    );
  }

  cancelDeployment(id: string) {
    return this.patch<{ message: string }>(`/deployments/${id}/cancel`, {});
  }

  buildQueue() {
    return this.get<{ queue: AdminDeployment[] }>("/system/build-queue");
  }

  statsTrends(days = 30) {
    return this.get<{
      deployments: { date: string; total: number; success: number; failed: number }[];
      users: { date: string; count: number }[];
    }>("/stats/trends", { days });
  }

  systemSummary() {
    return this.get<SystemSummary>("/system/summary");
  }

  systemRouting() {
    return this.get<SystemRouting>("/system/routing");
  }

  systemNodes() {
    return this.get<{ nodes: SystemNode[]; metricsAvailable: boolean }>("/system/nodes");
  }

  systemPods(namespace?: string) {
    return this.get<{ pods: SystemPod[]; metricsAvailable: boolean }>(
      "/system/pods",
      namespace ? { namespace } : undefined
    );
  }

  systemNamespaces() {
    return this.get<{ namespaces: SystemNamespace[] }>("/system/namespaces");
  }

  systemServices(namespace?: string) {
    return this.get<{ services: SystemService[] }>(
      "/system/services",
      namespace ? { namespace } : undefined
    );
  }

  systemEvents(namespace?: string) {
    return this.get<{ events: SystemEvent[] }>(
      "/system/events",
      namespace ? { namespace } : undefined
    );
  }

  systemPodServiceMap(namespace?: string) {
    return this.get<{ mapping: PodServiceEntry[] }>(
      "/system/pod-service-map",
      namespace ? { namespace } : undefined
    );
  }

  podLogs(namespace: string, name: string, opts?: { container?: string; lines?: number }) {
    return this.get<{ namespace: string; pod: string; container: string; lines: number; logs: string }>(
      `/system/pods/${namespace}/${name}/logs`,
      opts
    );
  }

  // ── Audit log ────────────────────────────────────────────────────────────

  auditLog(params?: {
    page?: number;
    limit?: number;
    actorId?: string;
    action?: string;
    targetId?: string;
    createdAfter?: string;
    createdBefore?: string;
  }) {
    return this.get<{ logs: AuditLogEntry[]; total: number; page: number; limit: number }>(
      "/audit",
      params
    );
  }
}

export const adminApi = new AdminApiClient();

// ── Shared admin types ───────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  accountStatus?: string;
  createdAt: string;
  lastLoginAt?: string;
  projectCount?: number;
  serviceCount?: number;
}

export interface AdminProject {
  _id?: string;
  id?: string;
  name: string;
  userId?: string;
  createdAt?: string;
  _memberRole?: string;
}

export interface AdminService {
  _id?: string;
  id?: string;
  name: string;
  type?: string;
  status?: string;
  framework?: string;
  userId?: string;
  projectId?: string;
  appSubdomain?: string;
  createdBy?: string;
  createdByEmail?: string;
  createdAt?: string;
}

export interface AdminDeployment {
  _id?: string;
  id?: string;
  serviceId?: string;
  serviceName?: string;
  projectId?: string;
  projectName?: string;
  userId?: string;
  triggeredBy?: string;
  triggeredByEmail?: string;
  status?: string;
  trigger?: string;
  gitCommit?: string;
  commitHash?: string;
  commitMessage?: string;
  branch?: string;
  stage?: string;
  createdAt?: string;
  duration?: number;
}

export interface SystemNode {
  name: string;
  status: string;
  roles: string;
  version: string;
  os: string;
  age: string;
  cpuCapacityMillis: number;
  memCapacityBytes: number;
  cpuUsageMillis: number;
  memUsageBytes: number;
  cpuPercent: number;
  memPercent: number;
}

export interface SystemPod {
  name: string;
  namespace: string;
  status: string;
  ready: string;
  node: string;
  restarts: number;
  age: string;
  cpuUsageMillis: number;
  memUsageBytes: number;
}

export interface SystemSummary {
  nodes: { total: number; ready: number };
  pods: { total: number; running: number; pending: number; failed: number };
  cpu: { capacityMillis: number; usageMillis: number; percent: number };
  memory: { capacityBytes: number; usageBytes: number; percent: number };
  buildsInProgress: number;
  metricsAvailable: boolean;
}

export interface SystemRouting {
  caddy: { reachable: boolean };
  customDomains: SystemCustomDomain[];
  platformSubdomains: SystemPlatformSubdomain[];
}

export interface SystemCustomDomain {
  domain: string;
  serviceId: string;
  serviceName: string;
  status: string;
  tlsStatus: string;
  nodeHost: string;
  nodePort: number;
  errorMessage?: string;
  checkedAt?: string;
  createdAt: string;
}

export interface SystemPlatformSubdomain {
  subdomain: string;
  serviceId: string;
  serviceName: string;
  projectId: string;
  projectName: string;
  serviceStatus: string;
}

export interface SystemNamespace {
  name: string;
  status: string;
  age: string;
}

export interface SystemService {
  name: string;
  namespace: string;
  type: string;
  clusterIP: string;
  ports: string;
  selector: Record<string, string>;
  age: string;
}

export interface SystemEvent {
  namespace: string;
  name: string;
  reason: string;
  message: string;
  object: string;
  count: number;
  age: string;
}

export interface PodServiceEntry {
  podName: string;
  namespace: string;
  podStatus: string;
  services: string[];
}

export interface AuditLogEntry {
  id: string;
  actorId: string;
  actorEmail: string;
  action: string;
  targetType?: string;
  targetId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
}
